import { useCallback, useEffect, useRef, useState } from "react";
import { CLI_REGISTRY } from "@/lib/cli-install-commands";
import {
  cancelRuntimeSetupRun,
  openRuntimeSetupSignInPage,
  startRuntimeSetupRun,
  watchRuntimeSetupRun,
  type RuntimeSetupRunEvent,
} from "@/desktop/onboarding/runtime-background-run";

export type CliLoginSignIn =
  | {
      loginId: string;
      phase: "running" | "awaiting_browser";
      /** The sign-in page is known: its window is open, or it can open in the browser. */
      signInPage: boolean;
      /** The user moved the sign-in to their browser. */
      inBrowser: boolean;
    }
  | { loginId: string; phase: "failed" };

interface ActiveRun {
  loginId: string;
  runId: string;
  stopWatching?: () => void;
}

/**
 * Signs an added Claude Code login in through the same native background
 * run onboarding uses, with the login's own folder. The page opens in a
 * Denker window with fresh web storage, so Claude asks for the account
 * instead of reusing the browser's; the user may move it to their browser.
 * Leaving Settings stops
 * listening but leaves the run going, so a sign-in finished in the browser
 * still lands; the logins list re-reads its status on focus.
 */
export function useCliLoginSignIn({
  executablePath,
  onFinished,
}: {
  executablePath: string;
  /** Called after a run ends, so the list can re-check the login. */
  onFinished: () => Promise<void>;
}) {
  const [state, setState] = useState<CliLoginSignIn | null>(null);
  const runRef = useRef<ActiveRun | null>(null);
  const latestFinished = useRef(onFinished);
  useEffect(() => {
    latestFinished.current = onFinished;
  });

  const finish = useCallback((run: ActiveRun): boolean => {
    if (runRef.current !== run) return false;
    runRef.current = null;
    run.stopWatching?.();
    return true;
  }, []);

  const handleEvent = useCallback(
    (run: ActiveRun, event: RuntimeSetupRunEvent) => {
      if (runRef.current !== run) return;
      if (event.type === "progress") {
        setState((previous) => ({
          loginId: run.loginId,
          phase: event.state,
          signInPage: event.sign_in_page,
          inBrowser:
            previous?.loginId === run.loginId &&
            previous.phase !== "failed" &&
            previous.inBrowser,
        }));
        return;
      }
      finish(run);
      setState(
        event.outcome === "failed" || event.outcome === "timed_out"
          ? { loginId: run.loginId, phase: "failed" }
          : null,
      );
      void latestFinished.current();
    },
    [finish],
  );

  const start = useCallback(
    async (loginId: string, windowTitle: string) => {
      if (runRef.current) return;
      const run: ActiveRun = {
        loginId,
        runId: `settings-cli-login-${loginId}-${Date.now()}`,
      };
      runRef.current = run;
      setState({
        loginId,
        phase: "running",
        signInPage: false,
        inBrowser: false,
      });
      try {
        const stopWatching = await watchRuntimeSetupRun(run.runId, (event) =>
          handleEvent(run, event),
        );
        if (runRef.current !== run) {
          stopWatching();
          return;
        }
        run.stopWatching = stopWatching;
        await startRuntimeSetupRun(run.runId, {
          choice: CLI_REGISTRY.claude,
          kind: "login",
          executablePath,
          loginId,
          signInWindowTitle: windowTitle,
        });
      } catch {
        if (finish(run)) setState({ loginId, phase: "failed" });
      }
    },
    [executablePath, finish, handleEvent],
  );

  const stop = useCallback(() => {
    const run = runRef.current;
    if (!run || !finish(run)) return;
    setState(null);
    void cancelRuntimeSetupRun(run.runId).catch(() => {});
  }, [finish]);

  /** Moves the sign-in from its window to the user's browser. */
  const openInBrowser = useCallback(() => {
    const run = runRef.current;
    if (!run) return;
    void openRuntimeSetupSignInPage(run.runId).then(
      () =>
        setState((previous) =>
          runRef.current === run &&
          previous?.loginId === run.loginId &&
          previous.phase !== "failed"
            ? { ...previous, inBrowser: true }
            : previous,
        ),
      () => {},
    );
  }, []);

  useEffect(
    () => () => {
      const run = runRef.current;
      runRef.current = null;
      run?.stopWatching?.();
    },
    [],
  );

  const active = state !== null && state.phase !== "failed";
  return { state, active, start, stop, openInBrowser };
}
